import { Injectable, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { DOCUMENT } from '@angular/common';
import { SSRCacheManager } from './ssr-cache.manager';
import { StaticCacheManager } from './static-cache.manager';
import { SentryLogger } from '@lentau/core/sentry/shared/services/sentry-logger.service';
import { ErrorLevel } from '@lentau/core/sentry/shared/services/sentry.level.const';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';
import { SESSION_STORAGE } from '@lentau/ui-kit/core/tokens/session-storage';

// Обновляет сервис-воркер при инициализации приложения или возобновлении приложения из фона
@Injectable({
  providedIn: 'root',
})
export class ServiceWorkerUpdateService {
  private readonly updateReloadThrottleKey: string = 'sw_update_reload_throttle';
  private readonly updateReloadThrottleMs: number = 60000;

  private readonly updates: SwUpdate = inject(SwUpdate, { optional: true });
  private readonly document: Document = inject(DOCUMENT);
  private readonly ssrCacheManager: SSRCacheManager = inject(SSRCacheManager);
  private readonly staticCacheManager: StaticCacheManager = inject(StaticCacheManager);
  private readonly sentryLogger: SentryLogger = inject(SentryLogger);
  private readonly browserApiSupportService: BrowserApiSupportService = inject(BrowserApiSupportService);
  private readonly sessionStorage: Storage = inject(SESSION_STORAGE);

  private isUpdateInProgress: boolean = false;

  public async update(reason: string = 'app_init'): Promise<void> {
    if (!this.updates || !this.updates.isEnabled || this.isUpdateInProgress || this.isReloadThrottled()) {
      return;
    }

    this.isUpdateInProgress = true;

    try {
      const hasNewVersion: boolean = await this.updates.checkForUpdate();

      if (!hasNewVersion) {
        return;
      }

      const isNewVersionActivated: boolean = await this.updates.activateUpdate();

      if (isNewVersionActivated) {
        void this.sentryLogger.handleError('ServiceWorker new version activated, reloading page', ErrorLevel.INFO, {
          reason,
        });

        // Очищаем кэши перед перезагрузкой страницы для чистого состояния
        this.ssrCacheManager.clearSSRCache();
        this.staticCacheManager.clearStaticCache();
        this.markReloadAttempt();

        this.document.location.reload();
      }
    } catch (error: unknown) {
      void this.sentryLogger.handleError(error, ErrorLevel.WARNING, {
        source: 'ServiceWorkerUpdateService.update',
        reason,
      });
    } finally {
      this.isUpdateInProgress = false;
    }
  }

  private isReloadThrottled(): boolean {
    if (this.browserApiSupportService.isServer()) {
      return false;
    }

    const rawLastReloadTime: string | null = this.sessionStorage.getItem(this.updateReloadThrottleKey);

    if (rawLastReloadTime === null) {
      return false;
    }

    const lastReloadTime: number = Number(rawLastReloadTime);

    if (Number.isNaN(lastReloadTime)) {
      return false;
    }

    return Date.now() - lastReloadTime <= this.updateReloadThrottleMs;
  }

  private markReloadAttempt(): void {
    if (this.browserApiSupportService.isServer()) {
      return;
    }

    this.sessionStorage.setItem(this.updateReloadThrottleKey, Date.now().toString());
  }
}
